<script>
  import { onMount } from 'svelte';
  import { COLORS, PADDING, clearCanvas } from './chartUtils.js';
  import { formatSignedPercent } from '../../../lib/strategyResult.js';

  // Return quantiles: { daily:{min,q1,median,q3,max}, weekly:{...}, monthly:{...} } (fractions; a box may be
  // all-null when too few observations). Three horizontal box-plots on a shared value axis (Tukey boxes).
  // New Canvas grammar — one of the previously-deferred optional charts, now built.
  export let data = { daily: null, weekly: null, monthly: null };

  let canvas;
  let ctx;
  let container;
  let width = 600;
  let height = 220;
  let hoverRow = -1;

  const fmt = (v) => formatSignedPercent(v);
  // Rows in display order; each carries its label + box (or null).
  $: rows = [
    { label: 'Daily', box: data?.daily },
    { label: 'Weekly', box: data?.weekly },
    { label: 'Monthly', box: data?.monthly },
  ];
  $: defined = rows.filter((r) => isBox(r.box));
  $: hasData = defined.length >= 1;
  $: domain = computeDomain(defined);
  $: summary = hasData
    ? `Return quantiles by period. ${defined.map((r) => `${r.label} median ${fmt(r.box.median)}`).join(', ')}.`
    : 'Return quantiles: insufficient data.';

  function isBox(b) {
    return b && b.min !== null && b.min !== undefined && b.max !== null && b.max !== undefined;
  }
  function computeDomain(defs) {
    let min = Infinity;
    let max = -Infinity;
    for (const r of defs) {
      if (r.box.min < min) min = r.box.min;
      if (r.box.max > max) max = r.box.max;
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: -1, max: 1 };
    if (min === max) { min -= 0.01; max += 0.01; }
    const pad = (max - min) * 0.08;
    return { min: min - pad, max: max + pad };
  }

  function resize() {
    if (!container) return;
    width = Math.max(200, container.offsetWidth);
    height = 220;
  }

  function draw() {
    if (!ctx || !hasData) return;
    clearCanvas(ctx, width, height);
    const plotW = width - PADDING.left - PADDING.right - 50; // extra left gutter for row labels
    const left = PADDING.left + 50;
    const xOf = (v) => left + ((v - domain.min) / (domain.max - domain.min)) * plotW;
    const rowH = (height - PADDING.top - PADDING.bottom) / rows.length;

    // Vertical gridlines + value labels.
    ctx.strokeStyle = COLORS.grid;
    ctx.fillStyle = COLORS.label;
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    const cols = 4;
    for (let i = 0; i <= cols; i++) {
      const v = domain.min + ((domain.max - domain.min) * i) / cols;
      const x = xOf(v);
      ctx.beginPath();
      ctx.moveTo(x, PADDING.top);
      ctx.lineTo(x, height - PADDING.bottom);
      ctx.stroke();
      ctx.fillText(fmt(v), x, height - 6);
    }

    // Zero reference.
    if (domain.min <= 0 && domain.max >= 0) {
      ctx.strokeStyle = COLORS.label;
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.moveTo(xOf(0), PADDING.top);
      ctx.lineTo(xOf(0), height - PADDING.bottom);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    rows.forEach((r, i) => {
      const cy = PADDING.top + rowH * (i + 0.5);
      // Row label.
      ctx.fillStyle = i === hoverRow ? '#e8e9ea' : COLORS.label;
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(r.label, left - 10, cy + 4);

      if (!isBox(r.box)) {
        ctx.fillStyle = COLORS.label;
        ctx.textAlign = 'left';
        ctx.fillText('insufficient data', left + 4, cy + 4);
        return;
      }

      const b = r.box;
      const boxTop = cy - rowH * 0.22;
      const boxH = rowH * 0.44;
      const q1x = xOf(b.q1 ?? b.min);
      const q3x = xOf(b.q3 ?? b.max);

      // Whiskers min..max.
      ctx.strokeStyle = COLORS.line;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(xOf(b.min), cy);
      ctx.lineTo(q1x, cy);
      ctx.moveTo(q3x, cy);
      ctx.lineTo(xOf(b.max), cy);
      ctx.stroke();
      // Whisker caps.
      ctx.beginPath();
      ctx.moveTo(xOf(b.min), cy - 5); ctx.lineTo(xOf(b.min), cy + 5);
      ctx.moveTo(xOf(b.max), cy - 5); ctx.lineTo(xOf(b.max), cy + 5);
      ctx.stroke();

      // IQR box.
      ctx.fillStyle = 'rgba(0,212,255,0.18)';
      ctx.fillRect(q1x, boxTop, Math.max(1, q3x - q1x), boxH);
      ctx.strokeStyle = COLORS.line;
      ctx.strokeRect(q1x, boxTop, Math.max(1, q3x - q1x), boxH);

      // Median.
      if (b.median !== null && b.median !== undefined) {
        const mx = xOf(b.median);
        ctx.strokeStyle = '#e8e9ea';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(mx, boxTop);
        ctx.lineTo(mx, boxTop + boxH);
        ctx.stroke();
      }
    });
  }

  $: if (ctx && canvas && hasData && width && height && rows && hoverRow >= -1) draw();

  let liveMsg = '';
  function announce(i) {
    const r = rows[i];
    liveMsg = r && isBox(r.box)
      ? `${r.label}: min ${fmt(r.box.min)}, median ${fmt(r.box.median)}, max ${fmt(r.box.max)}`
      : `${r?.label ?? ''}: insufficient data`;
  }
  function onMove(e) {
    if (!hasData) return;
    const rect = canvas.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const rowH = (height - PADDING.top - PADDING.bottom) / rows.length;
    hoverRow = Math.max(0, Math.min(rows.length - 1, Math.floor((y - PADDING.top) / rowH)));
    announce(hoverRow);
  }
  function onLeave() { hoverRow = -1; }

  function onKey(e) {
    if (!hasData) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const start = hoverRow < 0 ? 0 : hoverRow;
      hoverRow = Math.max(0, Math.min(rows.length - 1, start + (e.key === 'ArrowDown' ? 1 : -1)));
      announce(hoverRow);
    }
  }

  // Lazy 2D-context acquisition: the canvas renders inside {#if hasData}, so it may not exist at
  // mount (empty data — acquiring unconditionally threw and broke the whole reactive graph) and may
  // APPEAR later when data arrives. Acquire the context whenever the canvas binds.
  $: if (canvas && !ctx) { ctx = canvas.getContext('2d'); resize(); }

  onMount(() => {
    if (!canvas) return; // not rendered (empty state) — nothing to wire up
    if (canvas) ctx = canvas.getContext('2d');
    resize();
    draw();
    const onResize = () => { resize(); draw(); };
    window.addEventListener('resize', onResize);
    let pendingDraw;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && ctx && canvas) {
          clearTimeout(pendingDraw);
          pendingDraw = setTimeout(() => { resize(); draw(); }, 50);
        }
      });
    }, { threshold: 0.1 });
    if (container) observer.observe(container);
    return () => { window.removeEventListener('resize', onResize); observer.disconnect(); clearTimeout(pendingDraw); };
  });
</script>

<div class="bt-chart" bind:this={container}>
  <h4 class="bt-chart-title">Return Quantiles (daily / weekly / monthly)</h4>
  {#if hasData}
    <canvas
      bind:this={canvas}
      width={width}
      height={height}
      tabindex="0"
      aria-label={summary}
      on:mousemove={onMove}
      on:mouseleave={onLeave}
      on:keydown={onKey}
    ></canvas>
    <div class="bt-chart-live" aria-live="polite">{liveMsg}</div>
    <details class="bt-chart-data">
      <summary>View data as table</summary>
      <table>
        <thead><tr><th>Period</th><th>Min</th><th>Q1</th><th>Median</th><th>Q3</th><th>Max</th></tr></thead>
        <tbody>
          {#each rows as r}
            <tr>
              <td>{r.label}</td>
              {#if isBox(r.box)}
                <td>{fmt(r.box.min)}</td><td>{fmt(r.box.q1)}</td><td>{fmt(r.box.median)}</td>
                <td>{fmt(r.box.q3)}</td><td>{fmt(r.box.max)}</td>
              {:else}
                <td colspan="5">insufficient data</td>
              {/if}
            </tr>
          {/each}
        </tbody>
      </table>
    </details>
  {:else}
    <div class="empty-state"><p class="empty-state-description">Insufficient data for this chart.</p></div>
  {/if}
</div>

<style>
  @import './charts.css';
</style>
